/**
 * Clipboard Operations Utilities
 * Extracted from useNodeOperations.ts to eliminate duplication
 * Handles copy/cut/paste/duplicate operations for nodes
 */

import type { ProjectData, ProjectNode, NodeOperation } from '@/shared/types/projectData';
import { deepClone } from '@/shared/utils/clone';

function getNodeProjectId(node: ProjectNode, fallback: string | null): string | null {
  const namespace = node.id.includes(':') ? node.id.split(':')[0] : null;
  const projectId = node.projectId && node.projectId !== 'default' ? node.projectId : null;
  if (namespace && projectId && namespace !== projectId) return null;
  const owner = namespace || projectId || fallback;
  return owner && owner !== 'default' && !owner.includes(',') ? owner : null;
}

export function resolveCutNodes(
  projectData: ProjectData,
  sources: ProjectNode[],
  parentId: string,
  sourceProjectId: string | null
): ProjectNode[] | null {
  const nodesById = new Map(projectData.nodes.map(node => [node.id, node]));
  if (nodesById.size !== projectData.nodes.length) return null;
  const target = nodesById.get(parentId);
  if (!target || target.type !== 'group') return null;
  const targetProjectId = getNodeProjectId(target, projectData.projectId);
  if (!targetProjectId) return null;

  const selected = new Set<string>();
  for (const source of sources) {
    const current = nodesById.get(source.id);
    if (!current) continue;
    if (getNodeProjectId(source, sourceProjectId) !== targetProjectId ||
        getNodeProjectId(current, projectData.projectId) !== targetProjectId) return null;
    selected.add(current.id);
  }

  const roots: ProjectNode[] = [];
  for (const nodeId of selected) {
    const node = nodesById.get(nodeId)!;
    let ancestorId = node.parentId;
    const ancestors = new Set([nodeId]);
    let hasSelectedAncestor = false;
    while (ancestorId) {
      if (ancestors.has(ancestorId)) return null;
      ancestors.add(ancestorId);
      if (selected.has(ancestorId)) hasSelectedAncestor = true;
      ancestorId = nodesById.get(ancestorId)?.parentId;
    }
    if (!hasSelectedAncestor) roots.push(node);
  }

  const childrenByParent = new Map<string, ProjectNode[]>();
  for (const node of projectData.nodes) {
    if (!node.parentId) continue;
    const children = childrenByParent.get(node.parentId) || [];
    children.push(node);
    childrenByParent.set(node.parentId, children);
  }
  const result: ProjectNode[] = [];
  const visited = new Set<string>();
  const collect = (node: ProjectNode): boolean => {
    if (visited.has(node.id) || node.id === parentId ||
        getNodeProjectId(node, projectData.projectId) !== targetProjectId) return false;
    visited.add(node.id);
    result.push(node);
    const children = childrenByParent.get(node.id) || [];
    return children.sort((left, right) => left.order - right.order).every(collect);
  };
  return roots.every(collect) ? result : null;
}

/**
 * Copy nodes to clipboard
 * Does not modify project data, only stores nodes in clipboard
 */
export async function createCopyOperation(
  projectData: ProjectData,
  nodeIds: string[]
): Promise<NodeOperation | null> {
  if (!projectData || nodeIds.length === 0) return null;

  // Capture the selected subtree now; later copy/paste uses this snapshot.
  const selected = new Set(nodeIds);
  const nodesToCopy: ProjectNode[] = [];
  const captured = new Set<string>();
  const capture = (node: ProjectNode) => {
    if(captured.has(node.id))return;
    captured.add(node.id);nodesToCopy.push(node);
    projectData.nodes.filter(child=>child.parentId===node.id).sort((a,b)=>a.order-b.order).forEach(capture);
  };
  projectData.nodes.filter(node=>selected.has(node.id)).forEach(capture);

  if (nodesToCopy.length === 0) return null;

  // Store in clipboard
  const { clipboardStore } = await import('@/shared/stores/clipboardStore');
  clipboardStore.copy(nodesToCopy);

  // Create operation for history tracking (doesn't modify data)
  return {
    type: 'copy-nodes',
    nodeIds,
    clipboardData: nodesToCopy,
    timestamp: Date.now()
  };
}

/**
 * Cut nodes to clipboard
 * Marks nodes for deletion when pasted elsewhere
 */
export async function createCutOperation(
  projectData: ProjectData,
  nodeIds: string[]
): Promise<NodeOperation | null> {
  if (!projectData || nodeIds.length === 0) return null;

  // Get the nodes to cut
  const nodesToCut = nodeIds
    .map(id => projectData.nodes.find(n => n.id === id))
    .filter(Boolean) as ProjectNode[];

  if (nodesToCut.length === 0) return null;

  // Get parent ID (assuming all nodes have same parent for simplicity)
  const parentId = nodesToCut[0].parentId || null;

  // Store in clipboard
  const { clipboardStore } = await import('@/shared/stores/clipboardStore');
  clipboardStore.cut(nodesToCut, parentId, projectData.projectId);

  // Create operation for history tracking (doesn't delete yet)
  return {
    type: 'cut-nodes',
    nodeIds,
    clipboardData: nodesToCut,
    timestamp: Date.now()
  };
}

/**
 * Paste nodes from clipboard
 * Creates new nodes or moves cut nodes to target location
 */
export async function createPasteOperation(
  projectData: ProjectData,
  parentId: string,
  index?: number
): Promise<{ operation: NodeOperation | null; shouldClearClipboard: boolean; clipboardRevision?: number }> {
  if (!projectData) return { operation: null, shouldClearClipboard: false };

  const { clipboardStore } = await import('@/shared/stores/clipboardStore');
  if (!clipboardStore.hasContent()) {
    return { operation: null, shouldClearClipboard: false };
  }

  const wasCut = clipboardStore.wasCut();
  const sources = clipboardStore.get();
  const nodes = wasCut
    ? resolveCutNodes(projectData, sources, parentId, clipboardStore.getSourceProjectId())
    : sources;
  if (!nodes?.length) return { operation: null, shouldClearClipboard: false };
  const clipboardData = deepClone(nodes);
  if (wasCut) clipboardData.forEach(node => { delete node.children; });

  // Generate new IDs for pasted nodes
  const mappings = wasCut
    ? clipboardData.map(node => ({originalId: node.id, newId: node.id}))
    : clipboardStore.generateNewIds(clipboardData);

  // Create operation
  const operation: NodeOperation = {
    type: 'paste-nodes',
    nodeId: parentId,
    clipboardData,
    pasteParentId: parentId,
    pasteIndex: index || 0,
    duplicatedNodeMappings: mappings,
    cutNodeIds: wasCut ? clipboardData.map(n => n.id) : undefined,
    sourceNodeIds: wasCut ? clipboardData.filter(node => !clipboardData.some(parent=>parent.id===node.parentId)).map(node=>node.id) : undefined,
    metadata: wasCut ? { projectId: getNodeProjectId(nodes[0], projectData.projectId) } : undefined,
    originalIndex: wasCut ? undefined : index,
    timestamp: Date.now()
  };

  return {
    operation,
    shouldClearClipboard: wasCut,
    clipboardRevision: clipboardStore.getRevision()
  };
}

/**
 * Duplicate a single node
 * Creates a copy with new ID in the same parent
 */
export function createDuplicateNodeOperation(
  projectData: ProjectData,
  nodeId: string
): NodeOperation | null {
  if (!projectData) return null;

  const node = projectData.nodes.find(n => n.id === nodeId);
  if (!node) return null;

  const timestamp = Date.now();
  const newId = `${nodeId}-copy-${timestamp}`;

  // CRITICAL: Deep clone to prevent shared object references
  // Shallow copy causes title sync bug between original and duplicate
  const duplicatedNode = deepClone({ ...node, id: newId });

  return {
    type: 'duplicate-node',
    nodeId,
    data: duplicatedNode,
    parentId: node.parentId,
    targetIndex: undefined, // Will be placed after original
    duplicatedNodeMappings: [{ originalId: nodeId, newId }],
    timestamp
  };
}

/**
 * Get all descendant nodes recursively
 */
function getDescendants(projectData: ProjectData, nodeId: string): ProjectNode[] {
  const descendants: ProjectNode[] = [];
  const children = projectData.nodes.filter(n => n.parentId === nodeId);

  children.forEach(child => {
    descendants.push(child);
    descendants.push(...getDescendants(projectData, child.id));
  });

  return descendants;
}

/**
 * Duplicate an entire subtree (node + all descendants)
 * Creates copies of node and all children with new IDs
 */
export async function createDuplicateSubtreeOperation(
  projectData: ProjectData,
  nodeId: string
): Promise<NodeOperation | null> {
  if (!projectData) return null;

  const node = projectData.nodes.find(n => n.id === nodeId);
  if (!node) return null;

  // Get all descendants
  const allNodes = [node, ...getDescendants(projectData, nodeId)];

  const { clipboardStore } = await import('@/shared/stores/clipboardStore');
  const mappings = clipboardStore.generateNewIds(allNodes);

  return {
    type: 'duplicate-subtree',
    nodeId,
    clipboardData: allNodes,
    parentId: node.parentId,
    targetIndex: undefined,
    duplicatedNodeMappings: mappings,
    timestamp: Date.now()
  };
}
